import { ClipboardDocumentListIcon, DocumentTextIcon } from "@heroicons/react/24/outline";
import {
    ActivityCategory,
    ActivityRecordType,
    activityRecordTypeSingularLabels,
} from "@sp-crm/core";
import { activityCategoryIcons } from "components/tasks/activity-category-icons";
import { ActivityChip } from "components/tasks/activity-chip";
import { ClampedText } from "components/ui/clamped-text";
import { defaultLinkStyle } from "components/ui/link";
import { SecondaryCompactButton } from "components/ui/secondary-button";
import React from "react";
import { Link } from "react-router-dom";
import { breakNewlines } from "util/text";

export const activityRowIconClasses = "mt-0.5 h-6 w-6";

export const activityTimestampVerb = (category: ActivityCategory | null): string => {
    if (category === "call") {
        return "Called";
    }
    if (category === "sms" || category === "email") {
        return "Sent";
    }
    return "Completed";
};

interface ActivityRowsListProps {
    className: string;
    testId: string;
    children: React.ReactNode;
}

export const ActivityRowsList: React.FC<ActivityRowsListProps> = props => (
    <ol
        className={`divide-y divide-gray-100 ${props.className}`}
        data-testid={props.testId}>
        {props.children}
    </ol>
);

interface ActivityRowItemProps {
    leading: React.ReactNode;
    actions: React.ReactNode | null;
    children: React.ReactNode;
}

export const ActivityRowBody: React.FC<ActivityRowItemProps> = props => (
    <div className="flex items-start gap-2.5 py-2.5">
        <span className="w-7 flex-shrink-0 text-gray-400">{props.leading}</span>
        <div className="min-w-0 flex-1 text-sm">{props.children}</div>
        {props.actions ? (
            <div className="flex flex-shrink-0 items-center gap-1">{props.actions}</div>
        ) : null}
    </div>
);

interface ActivityRowEditButtonProps {
    onClick: () => void;
}

export const ActivityRowEditButton: React.FC<ActivityRowEditButtonProps> = props => (
    <SecondaryCompactButton onClick={props.onClick}>Edit</SecondaryCompactButton>
);

interface ActivityRowChipLineProps {
    category: ActivityCategory;
    label: string;
}

export const ActivityRowChipLine: React.FC<ActivityRowChipLineProps> = props => (
    <div className="mt-1">
        <ActivityChip category={props.category} label={props.label} />
    </div>
);

interface ActivityRowParentLinkProps {
    href: string;
    parentName: string;
    recordType: ActivityRecordType | null;
}

export const ActivityRowParentLink: React.FC<ActivityRowParentLinkProps> = props => (
    <span className="inline-flex flex-wrap items-center gap-x-1.5">
        <Link to={props.href} className={defaultLinkStyle}>
            {props.parentName}
        </Link>
        {props.recordType ? (
            <span className="text-xs text-gray-500">
                · {activityRecordTypeSingularLabels[props.recordType]}
            </span>
        ) : null}
    </span>
);

export interface ActivityRowRelatedEntity {
    href: string;
    name: string;
}

interface ActivityRowRelationsProps {
    href: string;
    parentName: string;
    recordType: ActivityRecordType | null;
    organization: ActivityRowRelatedEntity | null;
    involvedContacts: ActivityRowRelatedEntity[];
}

export const ActivityRowRelations: React.FC<ActivityRowRelationsProps> = props => (
    <span className="inline-flex flex-wrap items-center gap-x-1.5">
        <ActivityRowParentLink
            href={props.href}
            parentName={props.parentName}
            recordType={props.recordType}
        />
        {props.organization ? (
            <span className="inline-flex items-center gap-x-1">
                <span className="text-xs text-gray-500">at</span>
                <Link to={props.organization.href} className={defaultLinkStyle}>
                    {props.organization.name}
                </Link>
            </span>
        ) : null}
        {props.involvedContacts.length > 0 ? (
            <span className="inline-flex flex-wrap items-center gap-x-1">
                <span className="text-xs text-gray-500">with</span>
                {props.involvedContacts.map((contact, index) => (
                    <span key={contact.href}>
                        <Link to={contact.href} className={defaultLinkStyle}>
                            {contact.name}
                        </Link>
                        {index < props.involvedContacts.length - 1 ? "," : null}
                    </span>
                ))}
            </span>
        ) : null}
    </span>
);

export type ActivityRowIconKind = "task" | "note" | ActivityCategory;

export const activityRowIcons: Record<
    ActivityRowIconKind,
    React.ComponentType<React.SVGProps<SVGSVGElement>>
> = {
    ...activityCategoryIcons,
    task: ClipboardDocumentListIcon,
    note: DocumentTextIcon,
};

export interface ActivityRowDisplay {
    iconKind: ActivityRowIconKind;
    title: string | null;
    titleAccessory: React.ReactNode | null;
    chip: { category: ActivityCategory; label: string } | null;
    body: React.ReactNode | null;
    note: string | null;
    byline: React.ReactNode | null;
    timestamp: string;
    link: React.ReactNode | null;
}

interface ActivityRowTitleProps {
    title: string | null;
    accessory: React.ReactNode | null;
}

const ActivityRowTitle: React.FC<ActivityRowTitleProps> = ({ title, accessory }) => {
    if (!title) {
        return <>{accessory}</>;
    }
    if (accessory) {
        return (
            <div className="flex items-center gap-2">
                <span className="font-medium text-gray-900">{breakNewlines(title)}</span>
                {accessory}
            </div>
        );
    }
    return (
        <ClampedText lines={3}>
            <span className="font-medium text-gray-900">{breakNewlines(title)}</span>
        </ClampedText>
    );
};

const ActivityFeedRowContent: React.FC<{ display: ActivityRowDisplay }> = ({
    display,
}) => (
    <>
        <ActivityRowTitle title={display.title} accessory={display.titleAccessory} />
        {display.chip ? (
            <ActivityRowChipLine
                category={display.chip.category}
                label={display.chip.label}
            />
        ) : null}
        {display.body ? (
            <ClampedText lines={3}>
                <p className="text-sm text-gray-900">
                    {typeof display.body === "string"
                        ? breakNewlines(display.body)
                        : display.body}
                </p>
            </ClampedText>
        ) : null}
        {display.note ? (
            <div
                data-testid="activity-row-note"
                className="mt-1.5 border-l-2 border-gray-200 pl-2.5">
                <ClampedText lines={3}>
                    <p className="text-sm italic text-gray-600">
                        {breakNewlines(display.note)}
                    </p>
                </ClampedText>
            </div>
        ) : null}
        <p className="mt-0.5 text-xs text-gray-500">
            {display.byline ? <>{display.byline} · </> : null}
            {display.timestamp}
        </p>
        {display.link ? <div className="mt-1">{display.link}</div> : null}
    </>
);

interface ActivityFeedRowProps {
    display: ActivityRowDisplay;
    actions: React.ReactNode | null;
}

export const ActivityFeedRowBody: React.FC<ActivityFeedRowProps> = props => {
    const Icon = activityRowIcons[props.display.iconKind];
    return (
        <ActivityRowBody
            leading={<Icon aria-hidden="true" className={activityRowIconClasses} />}
            actions={props.actions}>
            <ActivityFeedRowContent display={props.display} />
        </ActivityRowBody>
    );
};
